import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatOption } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject, Observable, Subscription, combineLatest } from 'rxjs';
import { map, startWith, finalize } from 'rxjs/operators';
import { ContractWithProviderDoc, Organization, Provider } from 'src/typings';
import { ContractWithProviderService } from '../contract-with-provider.service';
import { NotificationService } from 'src/app/services/notification.service';
import { ContractWithProviderCreateDialogComponent } from './create-dialog/create-dialog.component';

@Component({
  selector: 'app-contract-with-provider-selector',
  templateUrl: './selector.component.html',
  styleUrls: ['./selector.component.scss']
})
export class ContractWithProviderSelectorComponent implements OnInit, OnDestroy {
  constructor(
    private contractService: ContractWithProviderService,
    private dialog: MatDialog,
    private notificationService: NotificationService
  ) {}

  private contracts$ = new BehaviorSubject<ContractWithProviderDoc[]>([]);

  filteredOptions: Observable<ContractWithProviderDoc[]> = new Observable();

  @Input() control: UntypedFormControl = new UntypedFormControl('');

  @Input() providerControl?: UntypedFormControl;

  @Input() orgControl?: UntypedFormControl;

  /**
   * По умолчанию организация ограничивает список договоров. В закупочных
   * распределениях она используется только как предпочтение для
   * автоподстановки: менеджер должен видеть договоры поставщика со всеми
   * организациями.
   */
  @Input() filterByOrg = true;

  @Input() appearance: 'legacy' | 'standard' | 'fill' | 'outline' = 'outline';

  @Input() label = 'Договор';

  @Input() showCreateButton = false;

  @Input() createDisabled = false;

  @ViewChild('createOption') createOption?: MatOption;

  creating = false;

  /**
   * Автоматически выбирает последний активный договор текущих
   * поставщика и организации, если менеджер ещё не указал другой.
   */
  @Input() autoSelect = false;

  private subscriptions = new Subscription();

  private loadRequestVersion = 0;

  private selectionRequestVersion = 0;

  ngOnInit(): void {
    this.loadContracts();

    this.filteredOptions = combineLatest([
      this.contracts$,
      this.control.valueChanges.pipe(startWith(''))
    ]).pipe(map(([contracts, value]) => this.filter(value, contracts)));

    if (this.providerControl) {
      this.subscriptions.add(
        this.providerControl.valueChanges.subscribe(() => {
          this.selectionRequestVersion += 1;
          if (!this.isCurrentContractMatch()) this.control.setValue('');
          this.loadContracts();
        })
      );
    }

    if (this.orgControl) {
      this.subscriptions.add(
        this.orgControl.valueChanges.subscribe(() => {
          this.selectionRequestVersion += 1;
          if (!this.isCurrentContractMatch()) this.control.setValue('');
          this.loadContracts();
        })
      );
    }
  }

  ngOnDestroy(): void {
    this.loadRequestVersion += 1;
    this.selectionRequestVersion += 1;
    this.subscriptions.unsubscribe();
  }

  displayProperty = (contract: ContractWithProviderDoc | string | null | undefined) => {
    if (!contract || typeof contract === 'string') return contract || '';
    return this.formatOption(contract);
  };

  formatOption(contract: ContractWithProviderDoc) {
    const number = contract.number ? `№ ${contract.number}` : 'Договор';
    const provider =
      typeof contract.provider === 'string'
        ? contract.provider
        : contract.provider?.description || '';
    const org = contract.org instanceof Object ? contract.org.name : contract.org || '';
    if (provider && org) return `${number} — ${provider} - ${org}`;
    if (provider) return `${number} — ${provider}`;
    return number;
  }

  private filter(
    value: string | ContractWithProviderDoc | null | undefined,
    contracts: ContractWithProviderDoc[]
  ): ContractWithProviderDoc[] {
    const lowerValue = this.displayProperty(value).toLowerCase();

    return contracts.filter((contract) => {
      if (!lowerValue) return true;
      return (
        contract.number?.toLowerCase().includes(lowerValue) ||
        this.formatOption(contract).toLowerCase().includes(lowerValue)
      );
    });
  }

  onSelect(event: MatAutocompleteSelectedEvent) {
    if (event.option === this.createOption) {
      this.openCreateDialog();
      return;
    }
    const contract = event.option.value as ContractWithProviderDoc;
    if (!contract) return;
    this.selectContract(contract);
  }

  private selectContract(contract: ContractWithProviderDoc) {
    const requestVersion = ++this.selectionRequestVersion;
    if (typeof contract.provider !== 'string') {
      if (this.doesContractMatch(contract)) this.control.setValue(contract);
      return;
    }
    this.contractService.getOne(contract.id).subscribe((full) => {
      if (requestVersion !== this.selectionRequestVersion || !this.doesContractMatch(full)) return;
      this.control.setValue(full);
    });
  }

  private getSelectedProvider(): Provider | null {
    const value = this.providerControl?.value;
    if (!value || typeof value === 'string' || !Number.isInteger(value.id) || value.id <= 0) return null;
    return value;
  }

  private getSelectedOrg(): Organization | null {
    const value = this.orgControl?.value;
    if (!value || typeof value === 'string' || !Number.isInteger(value.id) || value.id <= 0) return null;
    return value;
  }

  private loadContracts() {
    const requestVersion = ++this.loadRequestVersion;
    this.contracts$.next([]);
    if (!this.providerControl && !this.orgControl) {
      this.contractService.getList().subscribe((list) => {
        if (requestVersion !== this.loadRequestVersion) return;
        this.contracts$.next(list);
      });
      return;
    }

    const provider = this.getSelectedProvider();
    const org = this.getSelectedOrg();

    if (
      (this.providerControl && !provider) ||
      (this.orgControl && this.filterByOrg && !org)
    ) {
      this.contracts$.next([]);
      return;
    }

    const searchPayload: {
      provider_id?: number;
      org_id?: number;
      active?: boolean;
      limit: number;
      offset: number;
    } = {
      provider_id: provider?.id,
      org_id: this.filterByOrg ? org?.id : undefined,
      limit: 200,
      offset: 0
    };
    if (this.autoSelect) searchPayload.active = true;

    this.contractService
      .search(searchPayload)
      .subscribe((res) => {
        if (requestVersion !== this.loadRequestVersion) return;
        this.contracts$.next(res.data);
        if (this.autoSelect && !this.control.value && res.data.length) {
          this.selectContract(this.getAutoSelectContract(res.data, org));
        }
      });
  }

  private getAutoSelectContract(
    contracts: ContractWithProviderDoc[],
    preferredOrg: Organization | null
  ) {
    // Supplier purchasing preference takes precedence over the manager's
    // organization in selection/order allocations. Explicit organization filters
    // and already selected contracts remain unchanged.
    if (!this.filterByOrg) {
      const zenitDefault = contracts.find((contract) => {
        const provider = typeof contract.provider === 'string'
          ? contract.provider : contract.provider?.description;
        return provider?.trim().toLocaleLowerCase() === 'зенит' &&
          contract.number?.trim() === '4750' && contract.active !== false;
      });
      if (zenitDefault) return zenitDefault;
    }
    if (this.filterByOrg || !preferredOrg) return contracts[0];

    return (
      contracts.find((contract) => {
        if (typeof contract.org === 'string') return contract.org === preferredOrg.name;
        return contract.org?.id === preferredOrg.id;
      }) || contracts[0]
    );
  }

  private isCurrentContractMatch() {
    const current = this.control.value as ContractWithProviderDoc | string | null;
    if (!current || typeof current === 'string') return false;
    return this.doesContractMatch(current);
  }

  private doesContractMatch(current: ContractWithProviderDoc) {
    const provider = this.getSelectedProvider();
    const org = this.getSelectedOrg();
    const currentProvider = typeof current.provider === 'string' ? null : current.provider?.id;
    const currentOrg = typeof current.org === 'string' ? null : current.org?.id;
    const providerMatches =
      !this.providerControl || (!!provider && currentProvider === provider.id);
    const orgMatches =
      !this.orgControl || !this.filterByOrg || (!!org && currentOrg === org.id);
    return providerMatches && orgMatches;
  }

  openCreateDialog() {
    if (this.creating || this.createDisabled || this.control.disabled) return;
    const provider = this.getSelectedProvider();
    const org = this.getSelectedOrg();

    if (!provider || !org) {
      this.notificationService.warning('Выберите поставщика и организацию');
      return;
    }

    this.creating = true;
    const dialogRef = this.dialog.open(ContractWithProviderCreateDialogComponent, {
      width: '640px', maxWidth: 'calc(100vw - 32px)', maxHeight: 'calc(100vh - 32px)',
      data: {
        provider, org, lockContext: true,
        number: typeof this.control.value === 'string' ? this.control.value.trim() : ''
      }
    });

    this.subscriptions.add(dialogRef.afterClosed().pipe(
      finalize(() => this.creating = false)
    ).subscribe((doc: ContractWithProviderDoc | undefined) => {
      if (!doc?.id || this.createDisabled || this.control.disabled ||
        this.getSelectedProvider()?.id !== provider.id || this.getSelectedOrg()?.id !== org.id ||
        !this.doesContractMatch(doc)) return;
      // A late response for the old list must not replace the newly created option.
      this.loadRequestVersion += 1;
      this.selectionRequestVersion += 1;
      this.contracts$.next([doc, ...this.contracts$.value.filter(item => item.id !== doc.id)]);
      this.control.setValue(doc);
      this.control.markAsDirty();
    }));
  }

  get canSearch() {
    if (!this.providerControl && !this.orgControl) return true;
    return (
      (!this.providerControl || !!this.getSelectedProvider()) &&
      (!this.orgControl || !this.filterByOrg || !!this.getSelectedOrg())
    );
  }

  get canCreate() {
    return !!this.getSelectedProvider() && !!this.getSelectedOrg();
  }

  get missingFilterLabel() {
    if (this.providerControl && !this.getSelectedProvider()) return 'поставщика';
    if (this.orgControl && this.filterByOrg && !this.getSelectedOrg()) return 'организацию';
    return 'фильтр';
  }
}
